import { resolveId } from '@medplum/core'
import type { CarePlan, Reference } from '@medplum/fhirtypes'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { SignatureTaskCode } from '@seen/core/models/tasks/constants'
import type { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { TypographyOverline, TypographyPUI } from '@seen/ui'
import type { ReactNode } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { SignatureStatus } from '#src/components/signature/SignatureStatus.tsx'
import { useRead } from '#src/hooks/useRead.ts'

export function CarePlanCard({
  carePlan: carePlanRef,
  task: signatureTask,
}: {
  carePlan: Reference<CarePlan>
  task: SignatureTask
}): ReactNode {
  const { data: carePlan } = useRead(InitialCarePlan, carePlanRef)

  const isParticipantReview =
    signatureTask.signatureTaskCode === SignatureTaskCode.CARE_PLAN_PARTICIPANT

  if (!carePlan) {
    return <Loader />
  }

  return (
    <Link
      to={`/Patient/${resolveId(carePlan.patientSubject)}/care-plans/${carePlan.id}`}
    >
      <Card className="flex flex-col gap-4 p-4 hover:bg-muted">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-10">
            <div className="flex flex-col gap-1">
              <TypographyOverline>Care Plan</TypographyOverline>
              <TypographyPUI>{carePlan.title}</TypographyPUI>
            </div>
            <div className="flex flex-col gap-1">
              <TypographyOverline>Type</TypographyOverline>
              <TypographyPUI>{carePlan.typeDisplay}</TypographyPUI>
            </div>
            {carePlan.startDate ? (
              <div className="flex flex-col gap-1">
                <TypographyOverline>Start Date</TypographyOverline>
                <TypographyPUI>
                  <HoverDate date={carePlan.startDate} />
                </TypographyPUI>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <TypographyOverline>Created Date</TypographyOverline>
                <TypographyPUI>
                  <HoverDate date={carePlan.createdDate} />
                </TypographyPUI>
              </div>
            )}
            {carePlan.endDate ? (
              <div className="flex flex-col gap-1">
                <TypographyOverline>End Date</TypographyOverline>
                <TypographyPUI>
                  <HoverDate date={carePlan.endDate} />
                </TypographyPUI>
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              <TypographyOverline>Updated</TypographyOverline>
              <TypographyPUI>
                <HoverDate date={carePlan.lastUpdated} format="relative" />
              </TypographyPUI>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <SignatureStatus
            signature={signatureTask}
            isReview={isParticipantReview}
            withConfirmation={isParticipantReview}
          />
        </div>
      </Card>
    </Link>
  )
}
